import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { ClarifierQuestion } from '@/api/chat';
import { classNames as cn } from 'primereact/utils';
import { ImprovePromptIcon } from './ImprovePromptIcon';
import { SkipAllIcon } from './SkipAllIcon';
import { useFrostedGlassClass } from '@/ui/theme/useFrostedGlassClass';
import { HeaderIconButton } from '@/ui/pages/ChatPage/components/HeaderIconButton';

interface SelectedAnswer {
  questionIndex: number;
  answer: string;
  isCustom: boolean;
}

interface ImprovePromptQuestionsProps {
  questions: ClarifierQuestion[];
  onComplete: (answers: SelectedAnswer[]) => void;
}

export const ImprovePromptQuestions = ({ questions, onComplete }: ImprovePromptQuestionsProps) => {
  const frostedBgClass = useFrostedGlassClass();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<SelectedAnswer[]>([]);
  const [customInput, setCustomInput] = useState('');
  // Whether the custom answer input is active. Shows the selected style even when empty, and tells
  // `handleSkip` to save the typed text.
  const [isCustomActive, setIsCustomActive] = useState(false);
  const completedRef = useRef(false);

  const totalQuestions = questions.length;
  const currentQuestion = questions[currentQuestionIndex];
  const progressPercent = ((currentQuestionIndex + 1) / totalQuestions) * 100;

  const existingAnswer = useMemo(
    () => answers.find((a) => a.questionIndex === currentQuestionIndex),
    [answers, currentQuestionIndex],
  );

  // Restore the custom input for the current question from `answers`.
  useEffect(() => {
    const saved = answers.find((a) => a.questionIndex === currentQuestionIndex);
    if (saved?.isCustom) {
      setCustomInput(saved.answer);
      setIsCustomActive(true);
    } else {
      setCustomInput('');
      setIsCustomActive(false);
    }
    // Only on question change, so typing isn't overwritten.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestionIndex]);

  const handleCustomInputChange = useCallback(
    (value: string) => {
      setCustomInput(value);
      setIsCustomActive(true);
      // Save on every keystroke; an empty value removes the answer.
      setAnswers((prev) => {
        const filtered = prev.filter((a) => a.questionIndex !== currentQuestionIndex);
        const trimmed = value.trim();
        if (!trimmed) return filtered;
        return [...filtered, { questionIndex: currentQuestionIndex, answer: trimmed, isCustom: true }];
      });
    },
    [currentQuestionIndex],
  );

  const selectAnswer = useCallback(
    (answer: string, isCustom: boolean) => {
      setAnswers((prev) => {
        const filtered = prev.filter((a) => a.questionIndex !== currentQuestionIndex);
        return [...filtered, { questionIndex: currentQuestionIndex, answer, isCustom }];
      });

      if (currentQuestionIndex < totalQuestions - 1) {
        setTimeout(() => {
          setCurrentQuestionIndex((prev) => prev + 1);
        }, 200);
      } else {
        if (completedRef.current) return;
        completedRef.current = true;
        const finalAnswers = answers.filter((a) => a.questionIndex !== currentQuestionIndex);
        finalAnswers.push({ questionIndex: currentQuestionIndex, answer, isCustom });
        setTimeout(() => {
          onComplete(finalAnswers);
        }, 200);
      }
    },
    [currentQuestionIndex, totalQuestions, answers, onComplete],
  );

  const handleCustomSubmit = useCallback(() => {
    if (!customInput.trim()) return;
    selectAnswer(customInput.trim(), true);
  }, [customInput, selectAnswer]);

  const handleBack = useCallback(() => {
    // Auto-save handles persistence — the hydration effect will restore
    // whatever the user had typed on the previous question.
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  }, [currentQuestionIndex]);

  const handleSkip = useCallback(() => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    } else {
      if (completedRef.current) return;
      completedRef.current = true;
      onComplete(answers);
    }
  }, [currentQuestionIndex, totalQuestions, answers, onComplete]);

  const handleSkipAll = useCallback(() => {
    // "Skip all" sends the answers collected so far. Custom answers were
    // auto-saved on every keystroke, so nothing extra needs flushing here.
    if (completedRef.current) return;
    completedRef.current = true;
    onComplete(answers);
  }, [answers, onComplete]);

  if (!currentQuestion) return null;

  return (
    <div className={cn('border-theme-border-grey @container w-full rounded-3xl border p-5 shadow-sm', frostedBgClass)}>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ImprovePromptIcon />
          <span className="improve-prompt-label text-sm font-semibold">Improve prompt</span>
        </div>
        <div className="flex items-center gap-1">
          <HeaderIconButton
            icon={<i className="pi pi-arrow-left text-[0.875rem]" />}
            tooltip="Previous question"
            onClick={handleBack}
            disabled={currentQuestionIndex === 0}
          />
          <span className="text-theme-text-grey px-1 text-[0.8125rem] @max-[26.25rem]:hidden">
            Question {currentQuestionIndex + 1} of {totalQuestions}
          </span>
          <HeaderIconButton
            icon={<i className="pi pi-arrow-right text-[0.875rem]" />}
            tooltip="Skip question"
            onClick={handleSkip}
          />
          <HeaderIconButton
            icon={<SkipAllIcon className="h-[14px] w-[14px]" />}
            tooltip="Skip all"
            onClick={handleSkipAll}
          />
        </div>
      </div>

      <div className="mb-4 h-[2px] w-full overflow-hidden rounded-full" style={{ borderRadius: '2px' }}>
        <div
          className="h-full transition-all duration-300"
          style={{
            width: `${progressPercent}%`,
            borderRadius: '4px',
            background: 'linear-gradient(90deg, #FD6100 0%, #FF00FB 61.54%, #7B00FF 100%)',
          }}
        />
      </div>

      <h3 className="text-theme-black mb-3 text-base font-semibold">{currentQuestion.question}</h3>

      <div className="flex flex-col gap-2">
        {currentQuestion.answers.map((answerOption, idx) => {
          // A regular option is selected only if it's the answer and the custom input isn't active.
          const isSelected =
            !isCustomActive && existingAnswer?.answer === answerOption.answer && !existingAnswer?.isCustom;
          return (
            <button
              key={idx}
              onClick={() => selectAnswer(answerOption.answer, false)}
              className={cn(
                'text-theme-black flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-2 text-left text-sm transition-colors',
                isSelected
                  ? 'border-theme-border-grey bg-theme-background-grey'
                  : cn('hover:bg-theme-background-grey border-transparent', frostedBgClass),
              )}
            >
              <span className="text-theme-text-grey shrink-0 font-medium">{idx + 1}.</span>
              <span>{answerOption.answer}</span>
            </button>
          );
        })}

        <div
          className={cn(
            'flex items-center gap-2 rounded-lg border px-4 py-2 transition-colors',
            isCustomActive
              ? 'border-theme-border-grey bg-theme-background-grey'
              : cn(
                  'hover:bg-theme-background-grey focus-within:bg-theme-background-grey border-transparent',
                  frostedBgClass,
                ),
          )}
        >
          <span className="text-theme-text-grey shrink-0 text-sm font-medium">
            {currentQuestion.answers.length + 1}.
          </span>
          <input
            type="text"
            value={customInput}
            onChange={(e) => handleCustomInputChange(e.target.value)}
            onFocus={() => setIsCustomActive(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleCustomSubmit();
              }
            }}
            placeholder="Enter a custom response"
            className="text-theme-black placeholder-theme-text-grey min-w-0 flex-1 border-none bg-transparent text-sm outline-none"
          />
        </div>
      </div>
    </div>
  );
};

export type { SelectedAnswer };
